import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ArrowRight,
  ArrowLeft,
  Plus,
  X,
  CheckCircle2,
  Sparkles,
  Loader,
  User,
  Briefcase,
  GraduationCap,
  Wrench,
  Eye,
} from "lucide-react";

/**
 * Resume Builder Wizard (Bucket C).
 *
 * For brand-new users who don't have a resume yet — walks them through
 * Identity → Summary → Experience → Education → Skills → Review, then saves
 * the composed plain-text resume via /api/resume/text so the rest of the
 * Resmo flow (Studio, Tailor, etc.) can use it.
 *
 * Kept fully client-side; no new backend endpoint required.
 */

const STEPS = [
  { id: "identity", label: "Identity", icon: User },
  { id: "summary", label: "Summary", icon: Sparkles },
  { id: "experience", label: "Experience", icon: Briefcase },
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "skills", label: "Skills & Extras", icon: Wrench },
  { id: "review", label: "Review & Save", icon: Eye },
];

function emptyExp() {
  return { title: "", company: "", location: "", dates: "", bullets: [""] };
}
function emptyEdu() {
  return { degree: "", school: "", location: "", dates: "" };
}

function composeResumeText(state) {
  const lines = [];
  const { identity, summary, experience, education, skills } = state;

  // Header
  if (identity.name) lines.push(identity.name.toUpperCase());
  if (identity.headline) lines.push(identity.headline);
  const contact = [identity.email, identity.phone, identity.location, identity.linkedin]
    .filter(Boolean)
    .join(" · ");
  if (contact) lines.push(contact);
  lines.push("");

  if (summary?.trim()) {
    lines.push("SUMMARY");
    lines.push(summary.trim());
    lines.push("");
  }

  const expClean = (experience || []).filter(
    (e) => (e.title || "").trim() || (e.company || "").trim() || (e.bullets || []).some((b) => b.trim()),
  );
  if (expClean.length) {
    lines.push("EXPERIENCE");
    expClean.forEach((e) => {
      const head = [
        [e.title, e.company].filter(Boolean).join(" — "),
        [e.location, e.dates].filter(Boolean).join(" · "),
      ]
        .filter(Boolean)
        .join("  |  ");
      if (head) lines.push(head);
      (e.bullets || []).forEach((b) => {
        if (b.trim()) lines.push(`• ${b.trim()}`);
      });
      lines.push("");
    });
  }

  const eduClean = (education || []).filter(
    (e) => (e.degree || "").trim() || (e.school || "").trim(),
  );
  if (eduClean.length) {
    lines.push("EDUCATION");
    eduClean.forEach((e) => {
      const head = [
        [e.degree, e.school].filter(Boolean).join(" — "),
        [e.location, e.dates].filter(Boolean).join(" · "),
      ]
        .filter(Boolean)
        .join("  |  ");
      if (head) lines.push(head);
    });
    lines.push("");
  }

  if (skills.skills?.trim()) {
    lines.push("KEY SKILLS");
    lines.push(skills.skills.trim());
    lines.push("");
  }
  if (skills.certifications?.trim()) {
    lines.push("CERTIFICATIONS");
    skills.certifications
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((c) => lines.push(`• ${c}`));
    lines.push("");
  }
  if (skills.languages?.trim()) {
    lines.push("LANGUAGES");
    lines.push(skills.languages.trim());
    lines.push("");
  }
  if (skills.projects?.trim()) {
    lines.push("PROJECTS");
    skills.projects
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((p) => lines.push(`• ${p}`));
    lines.push("");
  }

  return lines.join("\n").trim();
}

export default function ResumeBuilder() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [state, setState] = useState({
    identity: { name: "", email: "", phone: "", location: "", headline: "", linkedin: "" },
    summary: "",
    experience: [emptyExp()],
    education: [emptyEdu()],
    skills: { skills: "", certifications: "", languages: "", projects: "" },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  const composed = composeResumeText(state);
  const canSave = composed.length >= 60;

  const goNext = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));

  const updateIdentity = (patch) =>
    setState((s) => ({ ...s, identity: { ...s.identity, ...patch } }));
  const updateSkills = (patch) =>
    setState((s) => ({ ...s, skills: { ...s.skills, ...patch } }));

  const updateExp = (i, patch) =>
    setState((s) => ({
      ...s,
      experience: s.experience.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    }));
  const addExp = () =>
    setState((s) => ({ ...s, experience: [...s.experience, emptyExp()] }));
  const removeExp = (i) =>
    setState((s) => ({
      ...s,
      experience: s.experience.length > 1 ? s.experience.filter((_, idx) => idx !== i) : s.experience,
    }));
  const updateBullet = (expIdx, bIdx, value) =>
    setState((s) => ({
      ...s,
      experience: s.experience.map((e, idx) =>
        idx === expIdx ? { ...e, bullets: e.bullets.map((b, bi) => (bi === bIdx ? value : b)) } : e,
      ),
    }));
  const addBullet = (expIdx) =>
    setState((s) => ({
      ...s,
      experience: s.experience.map((e, idx) =>
        idx === expIdx ? { ...e, bullets: [...e.bullets, ""] } : e,
      ),
    }));
  const removeBullet = (expIdx, bIdx) =>
    setState((s) => ({
      ...s,
      experience: s.experience.map((e, idx) =>
        idx === expIdx
          ? { ...e, bullets: e.bullets.length > 1 ? e.bullets.filter((_, bi) => bi !== bIdx) : e.bullets }
          : e,
      ),
    }));

  const updateEdu = (i, patch) =>
    setState((s) => ({
      ...s,
      education: s.education.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    }));
  const addEdu = () =>
    setState((s) => ({ ...s, education: [...s.education, emptyEdu()] }));
  const removeEdu = (i) =>
    setState((s) => ({
      ...s,
      education: s.education.length > 1 ? s.education.filter((_, idx) => idx !== i) : s.education,
    }));

  const handleSave = async () => {
    if (!canSave || saving) return;
    setError("");
    setSaving(true);
    try {
      await api.post("/resume/text", { content: composed });
      await refresh();
      navigate("/feed-job");
    } catch (e) {
      setSaving(false);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen rm-paper pb-20">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-6">
          <div className="rm-tag mb-3">Resume Builder · Bucket C</div>
          <h1 className="font-display text-3xl md:text-5xl uppercase leading-none">
            No resume yet? <span className="rm-text-orange">Let's build one.</span>
          </h1>
          <p className="text-sm md:text-base mt-2 max-w-2xl" style={{ color: "#475569" }}>
            Walk through six quick steps. We assemble a clean, ATS-friendly base resume that
            powers every tailored output you generate after this.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1" data-testid="builder-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < stepIdx;
            const active = i === stepIdx;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setStepIdx(i)}
                className="rm-btn text-xs px-3 py-1.5 flex-shrink-0"
                style={{
                  background: active ? "#0F1B2D" : done ? "#ECFDF5" : "white",
                  color: active ? "#FFC145" : done ? "#10B981" : "#0F1B2D",
                  borderColor: "#0F1B2D",
                }}
                data-testid={`builder-step-${s.id}`}
              >
                {done ? <CheckCircle2 size={12} /> : <Icon size={12} />}
                {i + 1}. {s.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Form panel */}
          <div className="lg:col-span-8">
            <div className="rm-card-static p-6 md:p-8" data-testid={`builder-panel-${step.id}`}>
              <div className="rm-label mb-3">
                Step {stepIdx + 1} of {STEPS.length}
              </div>
              <h2 className="font-display text-2xl md:text-3xl uppercase mb-5">{step.label}</h2>

              {step.id === "identity" && (
                <IdentityForm value={state.identity} onChange={updateIdentity} />
              )}
              {step.id === "summary" && (
                <SummaryForm value={state.summary} onChange={(v) => setState((s) => ({ ...s, summary: v }))} />
              )}
              {step.id === "experience" && (
                <ExperienceForm
                  items={state.experience}
                  onUpdate={updateExp}
                  onAdd={addExp}
                  onRemove={removeExp}
                  onUpdateBullet={updateBullet}
                  onAddBullet={addBullet}
                  onRemoveBullet={removeBullet}
                />
              )}
              {step.id === "education" && (
                <EducationForm
                  items={state.education}
                  onUpdate={updateEdu}
                  onAdd={addEdu}
                  onRemove={removeEdu}
                />
              )}
              {step.id === "skills" && (
                <SkillsForm value={state.skills} onChange={updateSkills} />
              )}
              {step.id === "review" && (
                <ReviewPanel composed={composed} canSave={canSave} />
              )}

              {error && (
                <div
                  className="mt-4 p-3 rounded-md font-mono-rm text-sm"
                  style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
                  data-testid="builder-error"
                >
                  {error}
                </div>
              )}

              <div
                className="flex items-center justify-between gap-3 mt-6 pt-4"
                style={{ borderTop: "2px dashed #0F1B2D" }}
              >
                <button
                  type="button"
                  onClick={goBack}
                  disabled={stepIdx === 0 || saving}
                  className="rm-btn rm-btn-ghost text-xs"
                  data-testid="builder-back-btn"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                {!isLast ? (
                  <button
                    type="button"
                    onClick={goNext}
                    className="rm-btn rm-btn-primary text-sm"
                    data-testid="builder-next-btn"
                  >
                    Next <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canSave || saving}
                    className="rm-btn rm-btn-primary text-sm"
                    data-testid="builder-save-btn"
                  >
                    {saving ? <Loader size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    {saving ? "Saving…" : "Save & continue to job"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Live preview panel */}
          <div className="lg:col-span-4">
            <div className="rm-card-static p-5 sticky top-24" data-testid="builder-preview-card">
              <div className="flex items-center gap-2 mb-3">
                <ResmoMascot state={canSave ? "satisfied" : "hungry"} size={64} />
                <div>
                  <div className="rm-label">Live preview</div>
                  <div className="font-mono-rm text-xs" style={{ color: "#475569" }}>
                    {composed.length} chars
                  </div>
                </div>
              </div>
              <pre
                className="font-mono-rm text-[11px] leading-snug whitespace-pre-wrap"
                style={{
                  background: "#FFFBEB",
                  border: "2px dashed #0F1B2D",
                  padding: 10,
                  maxHeight: 360,
                  overflow: "auto",
                }}
                data-testid="builder-preview-text"
              >
                {composed || "Your resume will appear here as you type…"}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testId, full }) {
  return (
    <label className={`block ${full ? "" : ""}`}>
      <div className="rm-label mb-1">{label}</div>
      {React.cloneElement(children, { "data-testid": testId })}
    </label>
  );
}

function IdentityForm({ value, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Full name" testId="builder-identity-name">
        <input className="rm-input" value={value.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Jane Doe" />
      </Field>
      <Field label="Headline / title" testId="builder-identity-headline">
        <input
          className="rm-input"
          value={value.headline}
          onChange={(e) => onChange({ headline: e.target.value })}
          placeholder="Senior Product Designer"
        />
      </Field>
      <Field label="Email" testId="builder-identity-email">
        <input className="rm-input" value={value.email} onChange={(e) => onChange({ email: e.target.value })} placeholder="jane@email.com" />
      </Field>
      <Field label="Phone" testId="builder-identity-phone">
        <input className="rm-input" value={value.phone} onChange={(e) => onChange({ phone: e.target.value })} placeholder="+1 555 555 5555" />
      </Field>
      <Field label="Location" testId="builder-identity-location">
        <input className="rm-input" value={value.location} onChange={(e) => onChange({ location: e.target.value })} placeholder="Brooklyn, NY" />
      </Field>
      <Field label="LinkedIn / portfolio" testId="builder-identity-linkedin">
        <input
          className="rm-input"
          value={value.linkedin}
          onChange={(e) => onChange({ linkedin: e.target.value })}
          placeholder="linkedin.com/in/jane"
        />
      </Field>
    </div>
  );
}

function SummaryForm({ value, onChange }) {
  return (
    <div>
      <Field label="Short summary (2-4 sentences)" testId="builder-summary-text">
        <textarea
          className="rm-textarea"
          style={{ minHeight: 140 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="A short pitch about who you are, what you build, and what you're after next. Keep it crisp."
        />
      </Field>
      <div className="font-mono-rm text-xs mt-2" style={{ color: "#475569" }}>
        Aim for 30-60 words. Lead with your strongest credential.
      </div>
    </div>
  );
}

function ExperienceForm({ items, onUpdate, onAdd, onRemove, onUpdateBullet, onAddBullet, onRemoveBullet }) {
  return (
    <div className="space-y-4">
      {items.map((exp, i) => (
        <div
          key={i}
          className="p-4 rounded-md space-y-2"
          style={{ border: "3px solid #0F1B2D", background: "white" }}
          data-testid={`builder-exp-${i}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="rm-label">Role #{i + 1}</div>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="rm-btn rm-btn-ghost text-xs px-2 py-1"
                data-testid={`builder-exp-${i}-remove`}
              >
                <X size={12} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              className="rm-input"
              placeholder="Job title"
              value={exp.title}
              onChange={(e) => onUpdate(i, { title: e.target.value })}
              data-testid={`builder-exp-${i}-title`}
            />
            <input
              className="rm-input"
              placeholder="Company"
              value={exp.company}
              onChange={(e) => onUpdate(i, { company: e.target.value })}
              data-testid={`builder-exp-${i}-company`}
            />
            <input
              className="rm-input"
              placeholder="Location (optional)"
              value={exp.location}
              onChange={(e) => onUpdate(i, { location: e.target.value })}
              data-testid={`builder-exp-${i}-location`}
            />
            <input
              className="rm-input"
              placeholder="Dates · e.g. Mar 2022 – Present"
              value={exp.dates}
              onChange={(e) => onUpdate(i, { dates: e.target.value })}
              data-testid={`builder-exp-${i}-dates`}
            />
          </div>
          <div className="space-y-1.5">
            <div className="rm-label">Achievements / bullets</div>
            {(exp.bullets || []).map((b, bi) => (
              <div key={bi} className="flex items-start gap-2">
                <span className="font-mono-rm text-sm pt-2">•</span>
                <textarea
                  className="rm-textarea"
                  style={{ minHeight: 50 }}
                  placeholder="Shipped X that drove Y by Z%"
                  value={b}
                  onChange={(e) => onUpdateBullet(i, bi, e.target.value)}
                  data-testid={`builder-exp-${i}-bullet-${bi}`}
                />
                {(exp.bullets || []).length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemoveBullet(i, bi)}
                    className="rm-btn rm-btn-ghost text-xs px-2 py-1 mt-2"
                    data-testid={`builder-exp-${i}-bullet-${bi}-remove`}
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => onAddBullet(i)}
              className="rm-btn rm-btn-ghost text-xs"
              data-testid={`builder-exp-${i}-add-bullet`}
            >
              <Plus size={12} /> Add bullet
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="rm-btn rm-btn-ghost text-xs"
        data-testid="builder-exp-add"
      >
        <Plus size={14} /> Add another role
      </button>
    </div>
  );
}

function EducationForm({ items, onUpdate, onAdd, onRemove }) {
  return (
    <div className="space-y-4">
      {items.map((ed, i) => (
        <div
          key={i}
          className="p-4 rounded-md grid grid-cols-1 md:grid-cols-2 gap-2"
          style={{ border: "3px solid #0F1B2D", background: "white" }}
          data-testid={`builder-edu-${i}`}
        >
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="rm-label">Degree #{i + 1}</div>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="rm-btn rm-btn-ghost text-xs px-2 py-1"
                data-testid={`builder-edu-${i}-remove`}
              >
                <X size={12} />
              </button>
            )}
          </div>
          <input
            className="rm-input"
            placeholder="Degree · e.g. B.S. Computer Science"
            value={ed.degree}
            onChange={(e) => onUpdate(i, { degree: e.target.value })}
            data-testid={`builder-edu-${i}-degree`}
          />
          <input
            className="rm-input"
            placeholder="School"
            value={ed.school}
            onChange={(e) => onUpdate(i, { school: e.target.value })}
            data-testid={`builder-edu-${i}-school`}
          />
          <input
            className="rm-input"
            placeholder="Location"
            value={ed.location}
            onChange={(e) => onUpdate(i, { location: e.target.value })}
            data-testid={`builder-edu-${i}-location`}
          />
          <input
            className="rm-input"
            placeholder="Dates · e.g. 2018 – 2022"
            value={ed.dates}
            onChange={(e) => onUpdate(i, { dates: e.target.value })}
            data-testid={`builder-edu-${i}-dates`}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="rm-btn rm-btn-ghost text-xs"
        data-testid="builder-edu-add"
      >
        <Plus size={14} /> Add another degree
      </button>
    </div>
  );
}

function SkillsForm({ value, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Key skills (comma-separated)" testId="builder-skills-list">
        <textarea
          className="rm-textarea"
          style={{ minHeight: 80 }}
          value={value.skills}
          onChange={(e) => onChange({ skills: e.target.value })}
          placeholder="Figma, design systems, prototyping, accessibility, user research"
        />
      </Field>
      <Field label="Certifications (one per line)" testId="builder-skills-certs">
        <textarea
          className="rm-textarea"
          style={{ minHeight: 70 }}
          value={value.certifications}
          onChange={(e) => onChange({ certifications: e.target.value })}
          placeholder={"AWS Certified Solutions Architect · 2024\nNN/g UX Certification · 2022"}
        />
      </Field>
      <Field label="Languages" testId="builder-skills-langs">
        <input
          className="rm-input"
          value={value.languages}
          onChange={(e) => onChange({ languages: e.target.value })}
          placeholder="English (native), Spanish (conversational)"
        />
      </Field>
      <Field label="Projects (one per line — optional)" testId="builder-skills-projects">
        <textarea
          className="rm-textarea"
          style={{ minHeight: 70 }}
          value={value.projects}
          onChange={(e) => onChange({ projects: e.target.value })}
          placeholder={"Resmo — AI resume tailoring tool · 2025\nOpen-source design tokens library · 2024"}
        />
      </Field>
    </div>
  );
}

function ReviewPanel({ composed, canSave }) {
  return (
    <div>
      <p className="text-sm mb-3" style={{ color: "#475569" }}>
        This is the plain-text version Resmo will store as your base resume. It will be used as the
        foundation for every tailored output you generate. You can always replace it later from{" "}
        <strong>Feed Resmo</strong>.
      </p>
      <pre
        className="font-mono-rm text-xs leading-snug whitespace-pre-wrap"
        style={{
          background: "#FFFBEB",
          border: "3px dashed #0F1B2D",
          padding: 14,
          maxHeight: 440,
          overflow: "auto",
        }}
        data-testid="builder-review-preview"
      >
        {composed || "Nothing to preview yet — go back and fill in at least your identity and one role."}
      </pre>
      {!canSave && (
        <div
          className="mt-3 font-mono-rm text-xs p-2 rounded"
          style={{ background: "#FEF3C7", color: "#78350F", border: "2px dashed #F59E0B" }}
        >
          Add a bit more content (target ~60 chars minimum) before saving.
        </div>
      )}
    </div>
  );
}
